// recordingBackup.js — IndexedDB mirror + cross-session recovery for interview
// recordings (Phase: auth hardening "b").
//
// Why: audio chunks are mirrored to IndexedDB as they're captured. If the page
// dies (crash / close / sleep / session-expiry mid-save), the recording is NOT
// lost — this module detects the un-finalized recording on next load and lets a
// human RE-UPLOAD the locally-stored chunks to the SAME recording_id and
// finalize the SAME interview (consent intact; no new interview is created).
//
// Invariants (per product owner):
//  - A recording's local copy is cleared ONLY after finalize is server-confirmed
//    (markFinalized → clearRecording) or by an explicit human Discard.
//  - Never auto-delete an un-recovered recording on a timer. The sticky banner
//    drives prompt recovery; if storage is tight we WARN, we don't delete.
//  - The chunk endpoint is idempotent by (recording_id, index), so re-uploading
//    is always safe.

import api, { getAccessToken, refreshAccessToken } from "./api";

const DB_NAME = "hireproof_recorder";
const DB_VERSION = 2; // v1 had only the chunks store; v2 adds the meta store
const CHUNK_STORE = "chunks";
const META_STORE = "meta";

// Headroom cap — we WARN above this, we never auto-delete (see module note).
export const MAX_RETAINED_RECORDINGS = 5;

function isQuotaError(e) {
  return !!e && (e.name === "QuotaExceededError" || e.code === 22 || e.code === 1014);
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "recordingId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function reqValue(request) {
  return new Promise((resolve) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(undefined);
  });
}

// ---- metadata -------------------------------------------------------------

export async function initRecordingMeta(meta) {
  try {
    const db = await openDB();
    const tx = db.transaction(META_STORE, "readwrite");
    tx.objectStore(META_STORE).put({
      recordingId: meta.recordingId,
      interviewId: meta.interviewId || null,
      candidateName: meta.candidateName || "Candidate",
      roundNumber: meta.roundNumber ?? null,
      mimeType: meta.mimeType || "audio/webm",
      startedAt: meta.startedAt || new Date().toISOString(),
      durationSeconds: 0,
      stopped: false,
      finalized: false,
    });
    await txDone(tx);
    db.close();
    return { ok: true };
  } catch (e) {
    return { ok: false, quota: isQuotaError(e) };
  }
}

async function updateMeta(recordingId, mutate) {
  try {
    const db = await openDB();
    // get → mutate → put must stay inside ONE active transaction. Issuing the
    // put synchronously from the get's onsuccess keeps the tx active (awaiting
    // between requests would let it auto-commit, silently dropping the put).
    await new Promise((resolve, reject) => {
      const tx = db.transaction(META_STORE, "readwrite");
      const store = tx.objectStore(META_STORE);
      const getReq = store.get(recordingId);
      getReq.onsuccess = () => {
        const m = getReq.result;
        if (m) {
          mutate(m);
          store.put(m);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    db.close();
  } catch {
    // best-effort
  }
}

export function markStopped(recordingId, durationSeconds) {
  return updateMeta(recordingId, (m) => {
    m.stopped = true;
    if (durationSeconds != null) m.durationSeconds = durationSeconds;
  });
}

export function markFinalized(recordingId) {
  return updateMeta(recordingId, (m) => {
    m.finalized = true;
  });
}

// ---- chunks ---------------------------------------------------------------

export async function saveChunk(recordingId, index, blob) {
  try {
    const db = await openDB();
    const tx = db.transaction(CHUNK_STORE, "readwrite");
    tx.objectStore(CHUNK_STORE).put({
      key: `${recordingId}:${index}`,
      recordingId,
      index,
      blob,
      uploaded: false,
    });
    await txDone(tx);
    db.close();
    return { ok: true };
  } catch (e) {
    return { ok: false, quota: isQuotaError(e) };
  }
}

export async function markChunkUploaded(recordingId, index) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(CHUNK_STORE, "readwrite");
      const store = tx.objectStore(CHUNK_STORE);
      const getReq = store.get(`${recordingId}:${index}`);
      getReq.onsuccess = () => {
        const rec = getReq.result;
        if (rec) {
          rec.uploaded = true;
          store.put(rec);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    db.close();
  } catch {
    // best-effort
  }
}

export async function getOrderedChunks(recordingId) {
  try {
    const db = await openDB();
    const tx = db.transaction(CHUNK_STORE, "readonly");
    const all = await reqValue(tx.objectStore(CHUNK_STORE).getAll());
    db.close();
    return (all || [])
      .filter((c) => c.recordingId === recordingId)
      .sort((a, b) => a.index - b.index);
  } catch {
    return [];
  }
}

// ---- recovery -------------------------------------------------------------

export async function clearRecording(recordingId) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction([CHUNK_STORE, META_STORE], "readwrite");
      const cstore = tx.objectStore(CHUNK_STORE);
      // Delete the meta synchronously; delete chunk keys from the getAllKeys
      // onsuccess so every request is issued while the tx is still active.
      tx.objectStore(META_STORE).delete(recordingId);
      const keysReq = cstore.getAllKeys();
      keysReq.onsuccess = () => {
        (keysReq.result || []).forEach((k) => {
          if (typeof k === "string" && k.startsWith(`${recordingId}:`)) cstore.delete(k);
        });
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    db.close();
  } catch {
    // best-effort
  }
}

// Un-finalized recordings that still have chunks on disk — the recovery set.
export async function listRecoverable() {
  try {
    const db = await openDB();
    const tx = db.transaction([META_STORE, CHUNK_STORE], "readonly");
    // Issue both reads synchronously (before any await) so the readonly tx
    // stays active for both; awaiting between them would deactivate it.
    const metasReq = tx.objectStore(META_STORE).getAll();
    const keysReq = tx.objectStore(CHUNK_STORE).getAllKeys();
    const [metas, chunkKeys] = await Promise.all([reqValue(metasReq), reqValue(keysReq)]);
    db.close();
    const haveChunks = new Set((chunkKeys || []).map((k) => String(k).split(":")[0]));
    return (metas || [])
      .filter((m) => !m.finalized && haveChunks.has(m.recordingId))
      .sort((a, b) => String(b.startedAt || "").localeCompare(String(a.startedAt || "")));
  } catch {
    return [];
  }
}

// Raw fetch upload (bypasses the axios interceptor) with bounded retry. The
// server upserts by (recording_id, index), so re-uploading is idempotent.
export async function uploadChunkToServer(recordingId, index, blob, { attempts = 3 } = {}) {
  const url = `${api.defaults.baseURL}/recordings/${recordingId}/chunks?index=${index}`;
  let lastErr;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    // Re-read the in-memory access token each attempt — it may have just been
    // refreshed below after a 401 (Phase 5 short-lived-token model).
    const token = getAccessToken();
    const headers = { "Content-Type": "application/octet-stream" };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers,
        body: blob,
        credentials: "include",
      });
      if (resp.status === 401) {
        // Access token expired during recovery re-upload — mint a fresh one
        // from the httpOnly refresh cookie, then retry. Keeps the IndexedDB
        // recovery loop working under short-lived tokens.
        await refreshAccessToken();
        throw new Error("chunk upload failed: 401");
      }
      if (!resp.ok) throw new Error(`chunk upload failed: ${resp.status}`);
      return await resp.json();
    } catch (e) {
      lastErr = e;
      if (attempt < attempts) {
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
  }
  throw lastErr;
}

// Ensure every locally-stored chunk is uploaded server-side (used both at
// stop-time to drain stragglers AND during recovery). Returns counts.
export async function ensureUploaded(recordingId, { onProgress } = {}) {
  const chunks = await getOrderedChunks(recordingId);
  let uploaded = 0;
  let failed = 0;
  for (const c of chunks) {
    if (c.uploaded) {
      uploaded += 1;
      onProgress?.({ uploaded, total: chunks.length, failed });
      continue;
    }
    try {
      await uploadChunkToServer(recordingId, c.index, c.blob);
      await markChunkUploaded(recordingId, c.index);
      uploaded += 1;
    } catch {
      failed += 1;
    }
    onProgress?.({ uploaded, total: chunks.length, failed });
  }
  return { total: chunks.length, uploaded, failed };
}

// Full recovery of one recording: re-upload all chunks, then finalize the
// EXISTING recording (consent/interview intact). Clears local copy ONLY on a
// server-confirmed finalize.
export async function recoverRecording(meta, { onProgress } = {}) {
  const result = await ensureUploaded(meta.recordingId, { onProgress });
  if (result.failed > 0 || result.total === 0) {
    return { ok: false, reason: result.total === 0 ? "no_chunks" : "upload_failed", result };
  }
  await api.post(
    `/recordings/${meta.recordingId}/finalize`,
    {
      duration_seconds: meta.durationSeconds || result.total * 20, // ~20s/chunk fallback
      mime_type: meta.mimeType || "audio/webm",
    },
    { _authMaxRetries: 3 },
  );
  await markFinalized(meta.recordingId);
  await clearRecording(meta.recordingId);
  return { ok: true, result };
}
